// src/components/correios/RequestReverseLogisticsModal.js
import React, { useState, useEffect } from "react";
import { X, Search, Loader2, ArrowRight, Check } from "lucide-react";
import { reverseLogisticsService } from "../../services/reverseLogisticsService";
import { pb } from "../../services/pocketbase";

export default function RequestReverseLogisticsModal({ isOpen, onClose, onSuccess }) {
  const [searchQuery, setSearchQuery] = useState("");
  const [searching, setSearching] = useState(false);
  const [foundOrders, setFoundOrders] = useState([]);
  const [selectedOrder, setSelectedOrder] = useState(null);

  // Form Fields
  const [customerName, setCustomerName] = useState("");
  const [customerCpf, setCustomerCpf] = useState("");
  const [serviceCode, setServiceCode] = useState("03301"); // Default to PAC Reverso 03301
  const [validityDays, setValidityDays] = useState(15);

  // Customer Address & Contact
  const [customerCep, setCustomerCep] = useState("");
  const [customerLogradouro, setCustomerLogradouro] = useState("");
  const [customerNumero, setCustomerNumero] = useState("");
  const [customerComplemento, setCustomerComplemento] = useState("");
  const [customerBairro, setCustomerBairro] = useState("");
  const [customerCidade, setCustomerCidade] = useState("");
  const [customerUf, setCustomerUf] = useState("");
  const [customerPhone, setCustomerPhone] = useState("");
  const [customerEmail, setCustomerEmail] = useState("");

  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!isOpen) {
      // Reset state on close
      setSearchQuery("");
      setFoundOrders([]);
      setSelectedOrder(null);
      setCustomerName("");
      setCustomerCpf("");
      setServiceCode("03301");
      setValidityDays(15);
      setCustomerCep("");
      setCustomerLogradouro("");
      setCustomerNumero("");
      setCustomerComplemento("");
      setCustomerBairro("");
      setCustomerCidade("");
      setCustomerUf("");
      setCustomerPhone("");
      setCustomerEmail("");
      setError("");
    }
  }, [isOpen]);

  if (!isOpen) return null;

  // Search orders from PocketBase
  const handleSearchOrders = async () => {
    if (!searchQuery.trim()) return;
    setSearching(true);
    setError("");
    try {
      const result = await pb.collection("production_orders").getList(1, 8, {
        filter: `order_number ~ "${searchQuery.trim()}" || customer_name ~ "${searchQuery.trim()}"`,
        sort: "-created",
      });
      setFoundOrders(result.items || []);
      if (result.items.length === 0) {
        setError("Nenhum pedido correspondente localizado.");
      }
    } catch (err) {
      console.error(err);
      setError("Erro ao pesquisar pedidos.");
    } finally {
      setSearching(false);
    }
  };

  const handleSelectOrder = (order) => {
    setSelectedOrder(order);
    
    // WooCommerce data mappings
    const customer = order.full_data?.order?.customer || order.full_data?.customer || {};
    const billing = order.full_data?.order?.billing || order.full_data?.billing || {};
    const shipping = order.full_data?.order?.shipping || order.full_data?.shipping || {};
    const customAddress = order.full_data?.shipping?.address || {};
    
    const billingName = `${billing.first_name || ""} ${billing.last_name || ""}`.trim();
    const shippingName = `${shipping.first_name || ""} ${shipping.last_name || ""}`.trim();
    
    setCustomerName(order.customer_name || customer.name || billingName || shippingName || "");
    
    const rawCpf = customer.cpf || customer.cnpj || billing.cpf || billing.cpf_cnpj || shipping.cpf || order.full_data?.order?.billing_cpf || "";
    setCustomerCpf(rawCpf);

    // Populate Address (prioritizing customAddress format)
    const address = (customAddress.street || customAddress.zip) ? customAddress : ((shipping.address_1 || shipping.street) ? shipping : billing);
    setCustomerCep((address.zip || address.postcode || "").replace(/\D/g, ""));
    setCustomerLogradouro(address.street || address.address_1 || "");
    setCustomerNumero(address.number || "");
    setCustomerComplemento(address.complemento || address.address_2 || "");
    setCustomerBairro(address.district || address.neighborhood || address.bairro || "");
    setCustomerCidade(address.city || "");
    setCustomerUf((address.state || address.statecode || address.uf || "").toUpperCase());

    // Populate Contact
    const rawPhone = customer.phone || billing.phone || shipping.phone || "";
    setCustomerPhone(rawPhone.replace(/\D/g, ""));
    setCustomerEmail(customer.email || billing.email || "cliente@email.com");

    setFoundOrders([]);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!selectedOrder) {
      setError("Selecione um pedido original.");
      return;
    }
    if (!customerName.trim()) {
      setError("O nome do cliente é obrigatório.");
      return;
    }
    if (!customerCpf.trim()) {
      setError("O CPF do cliente é obrigatório.");
      return;
    }
    if (!customerCep.trim() || customerCep.replace(/\D/g, "").length !== 8) {
      setError("O CEP do cliente deve ter 8 dígitos.");
      return;
    }

    setSubmitting(true);
    setError("");
    try {
      await reverseLogisticsService.createRequest({
        order_number: selectedOrder.order_number,
        customer_name: customerName,
        customer_cpf: customerCpf,
        service_code: serviceCode,
        validade_dias: Number(validityDays),
        customer_cep: customerCep,
        customer_logradouro: customerLogradouro,
        customer_numero: customerNumero || "SN",
        customer_complemento: customerComplemento,
        customer_bairro: customerBairro,
        customer_cidade: customerCidade,
        customer_uf: customerUf,
        customer_phone: customerPhone,
        customer_email: customerEmail,
      });

      alert("Logística reversa gerada com sucesso!");
      if (onSuccess) onSuccess();
      onClose();
    } catch (err) {
      console.error(err);
      setError(err.message || "Falha ao gerar código de logística reversa.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm flex justify-center items-center z-50 animate-fade-in p-4">
      <div className="bg-white rounded-2xl border border-slate-200 shadow-2xl w-full max-w-lg overflow-hidden flex flex-col max-h-[95vh] animate-scale-in">
        
        {/* Header */}
        <div className="px-6 py-4 border-b border-slate-100 flex justify-between items-center bg-slate-900 text-white">
          <h3 className="font-extrabold text-base uppercase tracking-wider">Nova Logística Reversa</h3>
          <button
            onClick={onClose}
            className="hover:bg-white/20 p-1.5 rounded-full transition-colors cursor-pointer"
          >
            <X size={18} />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto p-6 space-y-5 custom-scrollbar">
          
          {/* Search Section */}
          {!selectedOrder ? (
            <div className="space-y-3">
              <label className="block text-xs font-bold text-slate-700 uppercase">
                Passo 1: Pesquisar Pedido de Origem
              </label>
              <div className="flex gap-2">
                <div className="relative flex-1">
                  <input
                    type="text"
                    placeholder="Digite o número do pedido ou nome..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    onKeyDown={(e) => e.key === "Enter" && handleSearchOrders()}
                    className="w-full pl-9 pr-4 py-2 text-xs border border-slate-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500/20"
                  />
                  <Search className="absolute left-3 top-2.5 text-slate-400" size={14} />
                </div>
                <button
                  type="button"
                  onClick={handleSearchOrders}
                  disabled={searching}
                  className="px-4 py-2 bg-slate-800 hover:bg-black text-white text-xs font-bold rounded-xl transition-all cursor-pointer flex items-center gap-1.5"
                >
                  {searching ? <Loader2 size={13} className="animate-spin" /> : null}
                  Pesquisar
                </button>
              </div>

              {/* Found List */}
              {foundOrders.length > 0 && (
                <div className="border border-slate-200 rounded-xl divide-y divide-slate-150 overflow-hidden max-h-40 overflow-y-auto">
                  {foundOrders.map((o) => (
                    <button
                      key={o.id}
                      type="button"
                      onClick={() => handleSelectOrder(o)}
                      className="w-full text-left p-3 hover:bg-slate-50 transition-colors flex items-center justify-between text-xs cursor-pointer"
                    >
                      <div>
                        <span className="font-mono font-extrabold text-blue-600">#{o.order_number}</span>
                        <span className="font-bold text-slate-700 ml-2">{o.customer_name}</span>
                      </div>
                      <ArrowRight size={13} className="text-slate-400" />
                    </button>
                  ))}
                </div>
              )}
            </div>
          ) : (
            /* Selected Badge */
            <div className="bg-blue-50/50 border border-blue-200 rounded-xl p-4 flex justify-between items-center animate-fade-in">
              <div>
                <span className="text-[10px] bg-blue-600 text-white font-black px-2 py-0.5 rounded uppercase tracking-wider mr-2">
                  Origem Selecionada
                </span>
                <span className="font-mono font-black text-xs text-blue-800">#{selectedOrder.order_number}</span>
                <p className="text-xs font-semibold text-slate-600 mt-1">{selectedOrder.customer_name}</p>
              </div>
              <button
                onClick={() => setSelectedOrder(null)}
                className="text-xs text-red-600 hover:text-red-800 font-bold border border-red-200 bg-white px-2.5 py-1 rounded-lg hover:bg-red-50"
              >
                Alterar
              </button>
            </div>
          )}

          {/* Form Fields (Only active once order is selected) */}
          {selectedOrder && (
            <form onSubmit={handleSubmit} className="space-y-4 animate-fade-in">
              <label className="block text-xs font-bold text-slate-700 uppercase border-b pb-1">
                Passo 2: Confirmar Informações do Remetente e Serviço
              </label>

              {/* Identificação Básica */}
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-1">
                  <label className="text-[11px] font-bold text-slate-600">Nome do Remetente (Cliente)</label>
                  <input
                    type="text"
                    value={customerName}
                    onChange={(e) => setCustomerName(e.target.value)}
                    className="w-full px-3 py-2 text-xs border border-slate-300 rounded-xl bg-slate-50"
                    required
                  />
                </div>
                <div className="space-y-1">
                  <label className="text-[11px] font-bold text-slate-600">CPF/CNPJ do Remetente</label>
                  <input
                    type="text"
                    value={customerCpf}
                    onChange={(e) => setCustomerCpf(e.target.value)}
                    className="w-full px-3 py-2 text-xs border border-slate-300 rounded-xl bg-slate-50"
                    required
                  />
                </div>
              </div>

              {/* Endereço Completo */}
              <div className="bg-slate-550/10 border border-slate-200 rounded-xl p-3 space-y-3">
                <p className="text-[11px] font-extrabold text-slate-700 uppercase tracking-wide border-b pb-1">
                  Endereço do Remetente (Onde o cliente vai postar)
                </p>
                <div className="grid grid-cols-3 gap-3">
                  <div className="col-span-1 space-y-1">
                    <label className="text-[10px] font-bold text-slate-500 uppercase">CEP</label>
                    <input
                      type="text"
                      value={customerCep}
                      onChange={(e) => setCustomerCep(e.target.value)}
                      className="w-full px-2.5 py-1.5 text-xs border border-slate-300 rounded-lg bg-white"
                      required
                    />
                  </div>
                  <div className="col-span-2 space-y-1">
                    <label className="text-[10px] font-bold text-slate-500 uppercase">Logradouro</label>
                    <input
                      type="text"
                      value={customerLogradouro}
                      onChange={(e) => setCustomerLogradouro(e.target.value)}
                      className="w-full px-2.5 py-1.5 text-xs border border-slate-300 rounded-lg bg-white"
                      required
                    />
                  </div>
                </div>
                <div className="grid grid-cols-3 gap-3">
                  <div className="col-span-1 space-y-1">
                    <label className="text-[10px] font-bold text-slate-500 uppercase">Número</label>
                    <input
                      type="text"
                      value={customerNumero}
                      onChange={(e) => setCustomerNumero(e.target.value)}
                      className="w-full px-2.5 py-1.5 text-xs border border-slate-300 rounded-lg bg-white"
                      required
                    />
                  </div>
                  <div className="col-span-2 space-y-1">
                    <label className="text-[10px] font-bold text-slate-500 uppercase">Complemento</label>
                    <input
                      type="text"
                      value={customerComplemento}
                      onChange={(e) => setCustomerComplemento(e.target.value)}
                      className="w-full px-2.5 py-1.5 text-xs border border-slate-300 rounded-lg bg-white"
                    />
                  </div>
                </div>
                <div className="grid grid-cols-3 gap-3">
                  <div className="col-span-1 space-y-1">
                    <label className="text-[10px] font-bold text-slate-500 uppercase">Bairro</label>
                    <input
                      type="text"
                      value={customerBairro}
                      onChange={(e) => setCustomerBairro(e.target.value)}
                      className="w-full px-2.5 py-1.5 text-xs border border-slate-300 rounded-lg bg-white"
                      required
                    />
                  </div>
                  <div className="col-span-1 space-y-1">
                    <label className="text-[10px] font-bold text-slate-500 uppercase">Cidade</label>
                    <input
                      type="text"
                      value={customerCidade}
                      onChange={(e) => setCustomerCidade(e.target.value)}
                      className="w-full px-2.5 py-1.5 text-xs border border-slate-300 rounded-lg bg-white"
                      required
                    />
                  </div>
                  <div className="col-span-1 space-y-1">
                    <label className="text-[10px] font-bold text-slate-500 uppercase">UF</label>
                    <input
                      type="text"
                      value={customerUf}
                      onChange={(e) => setCustomerUf(e.target.value)}
                      className="w-full px-2.5 py-1.5 text-xs border border-slate-300 rounded-lg bg-white"
                      maxLength={2}
                      required
                    />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1">
                    <label className="text-[10px] font-bold text-slate-500 uppercase">Celular (Com DDD)</label>
                    <input
                      type="text"
                      value={customerPhone}
                      onChange={(e) => setCustomerPhone(e.target.value)}
                      className="w-full px-2.5 py-1.5 text-xs border border-slate-300 rounded-lg bg-white"
                      required
                    />
                  </div>
                  <div className="space-y-1">
                    <label className="text-[10px] font-bold text-slate-500 uppercase">Email de Notificação</label>
                    <input
                      type="email"
                      value={customerEmail}
                      onChange={(e) => setCustomerEmail(e.target.value)}
                      className="w-full px-2.5 py-1.5 text-xs border border-slate-300 rounded-lg bg-white"
                      required
                    />
                  </div>
                </div>
              </div>

              {/* Parâmetros do Serviço */}
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-1">
                  <label className="text-[11px] font-bold text-slate-600">Serviço dos Correios</label>
                  <select
                    value={serviceCode}
                    onChange={(e) => setServiceCode(e.target.value)}
                    className="w-full px-3 py-2 text-xs border border-slate-300 rounded-xl bg-white"
                  >
                    <option value="03301">PAC Reverso (03301)</option>
                    <option value="03247">SEDEX Reverso (03247)</option>
                    <option value="03174">SEDEX 12 Reverso (03174)</option>
                  </select>
                </div>
                <div className="space-y-1">
                  <label className="text-[11px] font-bold text-slate-600">Validade da Autorização</label>
                  <select
                    value={validityDays}
                    onChange={(e) => setValidityDays(Number(e.target.value))}
                    className="w-full px-3 py-2 text-xs border border-slate-300 rounded-xl bg-white"
                  >
                    <option value={15}>15 Dias</option>
                    <option value={30}>30 Dias</option>
                    <option value={7}>7 Dias</option>
                  </select>
                </div>
              </div>

              {/* Submit / Cancel Buttons */}
              <div className="flex gap-3 mt-6">
                <button
                  type="button"
                  onClick={onClose}
                  className="flex-1 py-3 bg-white hover:bg-slate-50 text-slate-700 font-extrabold rounded-xl border border-slate-300 transition-all cursor-pointer text-center text-xs uppercase tracking-wide"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  disabled={submitting}
                  className="flex-1 py-3 bg-emerald-600 hover:bg-emerald-700 text-white font-extrabold rounded-xl transition-all cursor-pointer flex items-center justify-center gap-1.5 shadow-md shadow-emerald-600/10 hover:scale-[1.01] active:scale-95 text-xs uppercase tracking-wide"
                >
                  {submitting ? (
                    <>
                      <Loader2 size={14} className="animate-spin" />
                      Gerando Autorização...
                    </>
                  ) : (
                    <>
                      <Check size={14} />
                      Gerar Autorização
                    </>
                  )}
                </button>
              </div>
            </form>
          )}

          {/* Error Message */}
          {error && (
            <div className="p-3 bg-red-50 border border-red-200 text-red-700 rounded-xl text-xs font-bold leading-tight">
              {error}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
